// Copyright Epic Games, Inc. All Rights Reserved.
import type { DocumentationPageProps } from '@pages/[...slug]';
import { UserPermissionsGatingWrapper } from './UserPermissionsGatingWrapper';
import { UserFlagsGatingWrapper } from './UserFlagsGatingWrapper';

const slugSegmentsFromPath = (slugPath?: string): string[] =>
  (slugPath ?? '')
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

export const withUserGating =
  (DocumentationPage: React.FC<DocumentationPageProps>) =>
  (props: DocumentationPageProps): React.ReactElement => {
    const permissions = props?.activeDocMeta?.protectedGroups?.permissions || [];
    const platform = props?.activeDocMeta?.protectedGroups?.meta?.instructionId || '';
    const flags = props?.activeDocMeta?.protectedGroups?.flags || [];
    const isProtected = permissions.length > 0 || flags.length > 0;

    // Wrap whenever a document is protected, regardless of WITH_USER_GATING. The static
    // props no longer contain the body for protected docs (see fetchDocumentPageProps);
    // the wrapper is what fetches it back from the authenticated endpoint. The endpoint
    // itself decides whether to enforce the AVS / LD check based on WITH_USER_GATING,
    // so dev environments without a gating stub still render real content.
    if (!isProtected) {
      return <DocumentationPage {...props} />;
    }

    const slug = slugSegmentsFromPath(props?.activeDocMeta?.slug);
    const { locale } = props;

    if (flags.length > 0) {
      return (
        <UserFlagsGatingWrapper flags={flags} slug={slug} locale={locale}>
          <DocumentationPage {...props} />
        </UserFlagsGatingWrapper>
      );
    }

    return (
      <UserPermissionsGatingWrapper platform={platform} slug={slug} locale={locale}>
        <DocumentationPage {...props} />
      </UserPermissionsGatingWrapper>
    );
  };
